import * as Yup from "yup";
import {
  EXTENDED_SPECIAL_CHARS,
  INPUT_LIMIT,
} from "../../../helpers/constants";
import {
  dateWithValidation,
  specialCharactersNotAllowedValidation,
  specialCharactersNotAllowedValidationForOptionalField,
  validEmailValidation,
} from "../../../helpers/constants/commonValidationSchemaUtils";
import { mandatoryCountriesForStateRegionProvince } from "../../../helpers/constants/countryFieldRulesUtils";
import { KeyValueMap } from "../../../redux/slices/appState/models";
import {
  CLIENT_SERVICES_EMAIL,
  CreateNISParticipantFormKey,
  VALUE_OTHER,
} from "./Models";

export const CreateNISParticipantSchema = (
  commonValidationMessages: KeyValueMap,
  userCountryDateFormat: string,
) =>
  Yup.object().shape(
    {
      // profile section
      [CreateNISParticipantFormKey.nisId]: Yup.string(),
      [CreateNISParticipantFormKey.participantFirstName]:
        specialCharactersNotAllowedValidation(
          commonValidationMessages.requiredValidation,
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        ).default(""),
      [CreateNISParticipantFormKey.participantLastName]:
        specialCharactersNotAllowedValidation(
          commonValidationMessages.requiredValidation,
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        ).default(""),
      [CreateNISParticipantFormKey.jobTitle]:
        specialCharactersNotAllowedValidationForOptionalField(
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        ).default(""),
      [CreateNISParticipantFormKey.participantEmail]: validEmailValidation(
        commonValidationMessages.invalidEmail,
      ).required(commonValidationMessages.requiredValidation),
      [CreateNISParticipantFormKey.participantWorkEmail]: Yup.string().when(
        [CreateNISParticipantFormKey.participantWorkEmail],
        {
          is: (participantWorkEmail: string) => {
            return !!participantWorkEmail;
          },
          then: (schema) =>
            schema.test(
              "valid-participant-work-email",
              commonValidationMessages.invalidEmail,
              (value) => {
                if (!value) return true; // Skip validation if the field is empty
                return validEmailValidation(
                  commonValidationMessages.invalidEmail,
                ).isValidSync(value);
              },
            ),
        },
      ),
      [CreateNISParticipantFormKey.participantMobile]: Yup.string(),
      [CreateNISParticipantFormKey.participantMobileCountryCode]: Yup.string(),
      [CreateNISParticipantFormKey.participantMobileISOCode]: Yup.string(),

      // professional details section
      [CreateNISParticipantFormKey.country]: Yup.string().required(
        commonValidationMessages.requiredValidation,
      ),
      [CreateNISParticipantFormKey.clientEmployer]: Yup.string().when(
        [CreateNISParticipantFormKey.clientEmployer],
        {
          is: (clientEmployer: string) => clientEmployer === VALUE_OTHER,
          then: (schema) => schema.optional(),
          otherwise: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
        },
      ),
      [CreateNISParticipantFormKey.clientEmployerOther]: Yup.string().when(
        [CreateNISParticipantFormKey.clientEmployer],
        {
          is: (clientEmployer: string) => clientEmployer === VALUE_OTHER,
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
          otherwise: (schema) => schema.optional(),
        },
      ),
      [CreateNISParticipantFormKey.projectManagerId]: Yup.string().when(
        [CreateNISParticipantFormKey.projectManagerId],
        {
          is: (projectManagerId: string) =>
            projectManagerId === CLIENT_SERVICES_EMAIL,
          then: (schema) => schema.optional(),
          otherwise: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
        },
      ),
      [CreateNISParticipantFormKey.employeeId]: Yup.string(),
      [CreateNISParticipantFormKey.releaseDate]: dateWithValidation(
        true,
        commonValidationMessages,
        false,
        false,
        userCountryDateFormat,
      ),
      [CreateNISParticipantFormKey.hrContact]: Yup.string(),
      [CreateNISParticipantFormKey.hrContactNameOther]: Yup.string()
        .when([CreateNISParticipantFormKey.hrContactNameOther], {
          is: (hrContactNameOther: string) => !!hrContactNameOther,
          then: (schema) =>
            schema
              .test(
                EXTENDED_SPECIAL_CHARS,
                commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
                (value) =>
                  !value ||
                  INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(
                    value,
                  ),
              )
              .test(
                "no-only-space",
                commonValidationMessages.alphabetFieldValidator,
                (value) => !value || value.trim().length !== 0,
              ),
        })
        .default(""),
      [CreateNISParticipantFormKey.hrContactEmailIdOther]: Yup.string().when(
        [CreateNISParticipantFormKey.hrContactEmailIdOther],
        {
          is: (hrContactEmailIdOther: string) => {
            return !!hrContactEmailIdOther;
          },
          then: (schema) =>
            schema.test(
              "valid-hr-contact-email-id-other",
              commonValidationMessages.invalidEmail,
              (value) => {
                if (!value) return true; // Skip validation if the field is empty
                return validEmailValidation(
                  commonValidationMessages.invalidEmail,
                ).isValidSync(value);
              },
            ),
        },
      ),
      [CreateNISParticipantFormKey.severanceStatusSigned]: Yup.string(),

      // address details section
      [CreateNISParticipantFormKey.address1]: Yup.string(),
      [CreateNISParticipantFormKey.address2]: Yup.string(),
      [CreateNISParticipantFormKey.address3]: Yup.string(),
      [CreateNISParticipantFormKey.countryAddress]: Yup.string(),
      [CreateNISParticipantFormKey.city]: Yup.string(),
      [CreateNISParticipantFormKey.postalCode]: Yup.string(),
      [CreateNISParticipantFormKey.stateProvince]: Yup.string()
        .when([CreateNISParticipantFormKey.countryAddress], {
          is: (addressCountry: string) => {
            return mandatoryCountriesForStateRegionProvince.includes(
              addressCountry,
            );
          },
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
        })
        .test(
          EXTENDED_SPECIAL_CHARS,
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
          (value) =>
            !value ||
            INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(value),
        ),
    },
    [
      [
        CreateNISParticipantFormKey.jobTitle,
        CreateNISParticipantFormKey.jobTitle,
      ],
      [
        CreateNISParticipantFormKey.participantWorkEmail,
        CreateNISParticipantFormKey.participantWorkEmail,
      ],
      [
        CreateNISParticipantFormKey.clientEmployer,
        CreateNISParticipantFormKey.clientEmployer,
      ],
      [
        CreateNISParticipantFormKey.clientEmployerOther,
        CreateNISParticipantFormKey.clientEmployer,
      ],
      [
        CreateNISParticipantFormKey.projectManagerId,
        CreateNISParticipantFormKey.projectManagerId,
      ],
      [
        CreateNISParticipantFormKey.hrContactEmailIdOther,
        CreateNISParticipantFormKey.hrContactEmailIdOther,
      ],
      [
        CreateNISParticipantFormKey.hrContactNameOther,
        CreateNISParticipantFormKey.hrContactNameOther,
      ],
    ],
  );
